import React, { JSX, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'hooks/AuthContext';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { MFAVerificationDialog } from './MFAVerificationDialog';
import { parseAuthErrorMessage } from './emailVerificationError';

export const LoginForm = (): JSX.Element => {
  const { login, mfaResponse, isLoading, error, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  useEffect(() => setErrorMessage(parseAuthErrorMessage(error)), [error]);

  const handleLoginSuccess = (): void => {
    navigate(user ? `/${user.userType}/dashboard` : '/');
  };

  const handleLogin = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (await login({ login: email, password })) handleLoginSuccess();
  };

  if (showForgotPassword) return <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />;

  return (
    <Box component="form" onSubmit={handleLogin} sx={{ mt: 1 }}>
      <TextField
        margin="normal"
        required
        fullWidth
        label="Email or Username"
        value={email}
        onChange={e => setEmail(e.target.value)}
        autoComplete="email"
        autoFocus
      />
      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={e => setPassword(e.target.value)}
        autoComplete="current-password"
        placeholder="Create a strong password"
        required
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                onMouseDown={e => e.preventDefault()}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box textAlign="right" sx={{ mt: 0.5 }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => setShowForgotPassword(true)}
          underline="hover"
          type="button"
        >
          Forgot password?
        </Link>
      </Box>
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}
      <Button
        type="submit"
        fullWidth
        variant="contained"
        disabled={isLoading}
        sx={{ mt: 3, mb: 2 }}
      >
        {isLoading ? <CircularProgress size={20} /> : 'Sign In'}
      </Button>
      <MFAVerificationDialog
        open={mfaResponse?.mfaRequired || false}
        onClose={() => undefined}
        onVerificationSuccess={handleLoginSuccess}
      />
    </Box>
  );
};
